// Serviço para gerenciar dados dos clientes
import api from './api';

// Chave para armazenar os clientes no localStorage
const CUSTOMERS_STORAGE_KEY = 'floratta_customers_v2';

/**
 * Busca um cliente pelo número de telefone/WhatsApp
 * @param {string} phone - Número de telefone do cliente
 * @returns {Promise<Object|null>} Dados do cliente ou null se não encontrado
 */
export const getCustomerByPhone = async (phone) => {
  try {
    // Primeiro tenta buscar do localStorage para evitar chamadas desnecessárias à API
    const localCustomers = getLocalCustomers();
    const normalizedPhone = normalizePhone(phone);
    
    // Busca no cache local
    const localCustomer = localCustomers.find(
      customer => normalizePhone(customer.phone) === normalizedPhone
    );
    
    if (localCustomer) {
      console.log('Cliente encontrado no cache local:', localCustomer);
      return localCustomer;
    }
    
    // Se não encontrar localmente, tenta buscar da API
    try {
      console.log('Cliente não encontrado no cache, buscando da API...');
      const response = await fetch(`http://localhost:8080/catalogo/api/customer.php?phone=${normalizedPhone}`);
      
      if (response.ok) {
        const data = await response.json();
        // Adiciona ao cache local
        saveCustomerToLocalStorage(data);
        return data;
      }
    } catch (apiError) {
      console.log('Cliente não encontrado na API');
      // Se não encontrar na API, é normal, apenas retorna null
    }
    
    return null;
  } catch (error) {
    console.error('Erro ao buscar cliente:', error);
    return null;
  }
};

/**
 * Mantém a função antiga para compatibilidade
 */
export const getCustomerByWhatsApp = getCustomerByPhone;

/**
 * Salva os dados do cliente
 * @param {Object} customerData - Dados do cliente
 * @returns {Promise<Object>} Dados do cliente salvos
 */
export const saveCustomer = async (customerData) => {
  try {
    // Converter whatsapp para phone se necessário (compatibilidade)
    const phone = customerData.phone || customerData.whatsapp;
    
    // Normaliza o número de telefone e garante que todos os campos existam
    const normalizedData = {
      ...customerData,
      phone: normalizePhone(phone),
      name: customerData.name || '',
      email: customerData.email || '',
      address: customerData.address || '',
      neighborhood: customerData.neighborhood || '',
      city: customerData.city || 'Belo Horizonte',
      state: customerData.state || 'MG',
      zipCode: customerData.zipCode || '',
      complement: customerData.complement || ''
    };
    
    // Remover campo whatsapp se existir (para evitar duplicidade)
    if (normalizedData.whatsapp) {
      delete normalizedData.whatsapp;
    }
    
    // Tenta salvar na API
    try {
      const response = await fetch('http://localhost:8080/catalogo/api/save_customer.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalizedData)
      });
      
      if (response.ok) {
        const data = await response.json();
        // Adiciona ao cache local
        saveCustomerToLocalStorage(data);
        return data;
      } else {
        throw new Error('Falha ao salvar cliente');
      }
    } catch (apiError) {
      console.error('Erro ao salvar cliente na API:', apiError);
      // Se falhar na API, salva apenas localmente
      saveCustomerToLocalStorage(normalizedData);
      return normalizedData;
    }
  } catch (error) {
    console.error('Erro ao salvar cliente:', error);
    throw error;
  }
};

/**
 * Atualiza os dados do cliente
 * @param {string} phone - Número de telefone do cliente
 * @param {Object} customerData - Novos dados do cliente
 * @returns {Promise<Object>} Dados do cliente atualizados
 */
export const updateCustomer = async (phone, customerData) => {
  try {
    const normalizedPhone = normalizePhone(phone);
    
    // Tenta atualizar na API
    try {
      const response = await api.put(`/customers/${normalizedPhone}`, customerData);
      
      // Atualiza no cache local
      updateCustomerInLocalStorage(normalizedPhone, response.data);
      return response.data;
    } catch (apiError) {
      console.error('Erro ao atualizar cliente na API:', apiError);
      // Se falhar na API, atualiza apenas localmente
      updateCustomerInLocalStorage(normalizedPhone, customerData);
      return customerData;
    }
  } catch (error) {
    console.error('Erro ao atualizar cliente:', error);
    throw error;
  }
};

// Funções auxiliares para gerenciar o cache local

/**
 * Obtém todos os clientes do localStorage
 * @returns {Array} Lista de clientes
 */
const getLocalCustomers = () => {
  try {
    const customersJson = localStorage.getItem(CUSTOMERS_STORAGE_KEY);
    return customersJson ? JSON.parse(customersJson) : [];
  } catch (error) {
    console.error('Erro ao obter clientes do localStorage:', error);
    return [];
  }
};

/**
 * Salva um cliente no localStorage
 * @param {Object} customer - Dados do cliente
 */
const saveCustomerToLocalStorage = (customer) => {
  try {
    const customers = getLocalCustomers();
    const normalizedPhone = normalizePhone(customer.phone);
    
    // Verifica se o cliente já existe
    const existingIndex = customers.findIndex(
      c => normalizePhone(c.phone) === normalizedPhone
    );
    
    if (existingIndex >= 0) {
      // Atualiza o cliente existente
      customers[existingIndex] = {
        ...customers[existingIndex],
        ...customer,
        lastUpdated: new Date().toISOString()
      };
    } else {
      // Adiciona novo cliente
      customers.push({
        ...customer,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });
    }
    
    // Salva no localStorage
    localStorage.setItem(CUSTOMERS_STORAGE_KEY, JSON.stringify(customers));
  } catch (error) {
    console.error('Erro ao salvar cliente no localStorage:', error);
  }
};

/**
 * Atualiza um cliente no localStorage
 * @param {string} phone - Número de telefone do cliente
 * @param {Object} customerData - Novos dados do cliente
 */
const updateCustomerInLocalStorage = (phone, customerData) => {
  try {
    const customers = getLocalCustomers();
    const normalizedPhone = normalizePhone(phone);
    
    // Encontra o índice do cliente
    const index = customers.findIndex(
      c => normalizePhone(c.phone) === normalizedPhone
    );
    
    if (index >= 0) {
      // Atualiza o cliente
      customers[index] = {
        ...customers[index],
        ...customerData,
        lastUpdated: new Date().toISOString()
      };
      
      // Salva no localStorage
      localStorage.setItem(CUSTOMERS_STORAGE_KEY, JSON.stringify(customers));
    }
  } catch (error) {
    console.error('Erro ao atualizar cliente no localStorage:', error);
  }
};

/**
 * Normaliza um número de telefone removendo caracteres não numéricos
 * @param {string} phone - Número de telefone
 * @returns {string} Número de telefone normalizado
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Formata um número de telefone para exibição
 * @param {string} phone - Número de telefone
 * @returns {string} Número de telefone formatado
 */
export const formatPhone = (phone) => {
  const normalized = normalizePhone(phone);
  
  if (!normalized) return '';
  
  // Formato brasileiro: (XX) XXXXX-XXXX
  if (normalized.length === 11) {
    return `(${normalized.substring(0, 2)}) ${normalized.substring(2, 7)}-${normalized.substring(7)}`;
  }
  
  // Outros formatos ou incompletos
  return normalized;
};

// Manter funções antigas para compatibilidade
export const normalizeWhatsApp = normalizePhone;
export const formatWhatsApp = formatPhone;

/**
 * Incrementa o contador de pedidos de um cliente
 * @param {string} phone - Número de telefone do cliente
 * @returns {Promise<Object>} Dados do cliente atualizados
 */
export const incrementOrderCount = async (phone) => {
  try {
    const normalizedPhone = normalizePhone(phone);
    
    // Tenta atualizar na API
    try {
      const response = await api.post(`/customers/${normalizedPhone}/order`);
      
      // Atualiza no cache local
      updateCustomerInLocalStorage(normalizedPhone, response.data);
      return response.data;
    } catch (apiError) {
      console.error('Erro ao incrementar contador de pedidos na API:', apiError);
      return null;
    }
  } catch (error) {
    console.error('Erro ao incrementar contador de pedidos:', error);
    return null;
  }
};
